/**
 * The calendar's reads (`111-frontend-architecture.md` §3, `080-calendar.md` §2).
 *
 * **One request per range.** The agenda, the week and the month each compute a `[from, to)`
 * and ask for it once; `GET /api/calendar` returns events AND page due dates in that one
 * response (`140-roadmap.md` §M5, criterion 4: "rendering one week of the calendar issues
 * exactly one `GET /api/calendar` call — not one for events and one for dues").
 *
 * Because the key IS the range, the three views share the cache wherever they legitimately
 * overlap, and a range change is a new key — which is what makes a refetch happen at all.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { keys } from '@/api/keys';
import {
  getCalendar,
  getEventCategories,
  getPageEvents,
  type CalendarResponse,
  type EventCategoriesResponse,
  type PageEventsResponse,
} from '@/api/endpoints/calendar';

export function useCalendarRange(
  from: string,
  to: string,
  enabled = true,
): UseQueryResult<CalendarResponse> {
  return useQuery({
    queryKey: keys.calendar.range(from, to),
    queryFn: () => getCalendar(from, to, true),
    enabled,
    // `111` §3.2.
    staleTime: 60_000,
    gcTime: 10 * 60_000,
  });
}

/** Effectively static (`111` §3.2). Invalidated on the rare category CRUD. */
export function useEventCategories(): UseQueryResult<EventCategoriesResponse> {
  return useQuery({
    queryKey: keys.eventCategories(),
    queryFn: getEventCategories,
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

/** The page → events direction (`080` §8). Rendered beside the backlinks panel. */
export function usePageEvents(id: string | undefined): UseQueryResult<PageEventsResponse> {
  return useQuery({
    queryKey: keys.pages.events(id ?? ''),
    queryFn: () => getPageEvents(id as string),
    enabled: id !== undefined,
    staleTime: 60_000,
    gcTime: 10 * 60_000,
  });
}
